import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import ImageViewer from './components/ImageViewer';
import AnnotationToolkit from './components/AnnotationToolkit';
import ImageComparison from './components/ImageComparison';
import CaseDocumentation from './components/CaseDocumentation';
import ImageLibrary from './components/ImageLibrary';
import XrayMachineConnection from './components/XrayMachineConnection';
import AppImage from '../../components/AppImage';

import Button from '../../components/ui/Button';
import ToothDiagram from './components/ToothDiagram';
import ToothNotesModal from './components/ToothNotesModal';


const DentalImagingManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('viewer'); // viewer, comparison, library
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAnnotationTools, setShowAnnotationTools] = useState(true);
  const [showCasePanel, setShowCasePanel] = useState(true);
  const [annotations, setAnnotations] = useState([]);
  const [viewerMode, setViewerMode] = useState('2d'); // 2d, 3d, ct
  const [showXrayConnection, setShowXrayConnection] = useState(false);
  const [toothNotes, setToothNotes] = useState({});
  const [showToothModal, setShowToothModal] = useState(false);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [showMobileLibrary, setShowMobileLibrary] = useState(false);
  const [showMobileTools, setShowMobileTools] = useState(false);

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "48 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: "warning",
      title: "X-ray Review Pending",
      message: "Bitewing radiographs from 01/15/2026 require review and annotation",
      dismissible: true
    }
  ];

  const imagingData = [
    // Bitewing X-rays
    {
      id: 1,
      type: 'xray',
      category: 'Bitewing',
      toothNumber: '#14-15',
      date: '2026-01-15',
      time: '09:30 AM',
      provider: 'Dr. Chen',
      status: 'pending_review',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23e5e7eb'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%236b7280'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%236b7280'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%236b7280' text-anchor='middle'%3EBitewing X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Right posterior bitewing X-ray showing upper and lower molars with clear interproximal spaces for caries detection',
      annotations: 2,
      notes: 'Routine checkup - right posterior'
    },
    {
      id: 2,
      type: 'xray',
      category: 'Bitewing',
      toothNumber: '#18-19',
      date: '2026-01-15',
      time: '09:32 AM',
      provider: 'Dr. Chen',
      status: 'pending_review',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23e5e7eb'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%236b7280'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%236b7280'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%236b7280' text-anchor='middle'%3EBitewing X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Left posterior bitewing X-ray displaying molars and premolars with visible bone levels and tooth contacts',
      annotations: 1,
      notes: 'Routine checkup - left posterior'
    },
    {
      id: 3,
      type: 'xray',
      category: 'Bitewing',
      toothNumber: '#3-5',
      date: '2026-01-15',
      time: '09:34 AM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23e5e7eb'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%236b7280'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%236b7280'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%236b7280' text-anchor='middle'%3EBitewing X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Anterior bitewing X-ray showing front teeth with visible interproximal areas and bone structure',
      annotations: 0,
      notes: 'Routine checkup - anterior region'
    },

    // Periapical X-rays
    {
      id: 4,
      type: 'xray',
      category: 'Periapical',
      toothNumber: '#3',
      date: '2026-01-12',
      time: '02:15 PM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dbeafe'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%233b82f6'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%233b82f6'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%233b82f6' text-anchor='middle'%3EPeriapical X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Periapical X-ray of upper right first molar showing complete root structure, periapical area, and surrounding bone',
      annotations: 5,
      notes: 'Caries detected - treatment planning'
    },
    {
      id: 5,
      type: 'xray',
      category: 'Periapical',
      toothNumber: '#8-9',
      date: '2026-01-14',
      time: '10:45 AM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dbeafe'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%233b82f6'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%233b82f6'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%233b82f6' text-anchor='middle'%3EPeriapical X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Periapical X-ray of central incisors showing root canals, apex, and surrounding bone structure',
      annotations: 3,
      notes: 'Root canal evaluation'
    },
    {
      id: 6,
      type: 'xray',
      category: 'Periapical',
      toothNumber: '#19',
      date: '2026-01-13',
      time: '03:20 PM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dbeafe'/%3E%3Cpath d='M200 120l-30 30h20v30h20v-30h20z' fill='%233b82f6'/%3E%3Ccircle cx='200' cy='100' r='15' fill='%233b82f6'/%3E%3Ctext x='200' y='220' font-family='Arial' font-size='14' fill='%233b82f6' text-anchor='middle'%3EPeriapical X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Periapical X-ray of lower left first molar displaying bifurcated roots and periapical region',
      annotations: 4,
      notes: 'Periapical lesion assessment'
    },

    // Panoramic X-rays
    {
      id: 7,
      type: 'xray',
      category: 'Panoramic',
      toothNumber: 'Full Mouth',
      date: '2026-01-05',
      time: '10:20 AM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='300' viewBox='0 0 600 300'%3E%3Crect width='600' height='300' fill='%23fef3c7'/%3E%3Cellipse cx='300' cy='150' rx='250' ry='100' fill='none' stroke='%23f59e0b' stroke-width='3'/%3E%3Ctext x='300' y='220' font-family='Arial' font-size='14' fill='%23f59e0b' text-anchor='middle'%3EPanoramic X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Panoramic X-ray showing complete view of upper and lower dental arches, TMJ joints, and maxillary sinuses',
      annotations: 12,
      notes: 'Annual screening - comprehensive evaluation'
    },
    {
      id: 8,
      type: 'xray',
      category: 'Panoramic',
      toothNumber: 'Full Mouth',
      date: '2025-12-20',
      time: '11:15 AM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='300' viewBox='0 0 600 300'%3E%3Crect width='600' height='300' fill='%23fef3c7'/%3E%3Cellipse cx='300' cy='150' rx='250' ry='100' fill='none' stroke='%23f59e0b' stroke-width='3'/%3E%3Ctext x='300' y='220' font-family='Arial' font-size='14' fill='%23f59e0b' text-anchor='middle'%3EPanoramic X-ray%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Panoramic X-ray displaying impacted wisdom teeth in all four quadrants with surrounding bone structure',
      annotations: 8,
      notes: 'Wisdom teeth evaluation - surgical planning'
    },

    // Cone Beam CT Scans
    {
      id: 9,
      type: 'ct',
      category: 'Cone Beam CT',
      toothNumber: 'Full Arch',
      date: '2026-01-10',
      time: '11:00 AM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23ddd6fe'/%3E%3Ccircle cx='200' cy='150' r='80' fill='none' stroke='%238b5cf6' stroke-width='3'/%3E%3Ccircle cx='200' cy='150' r='50' fill='none' stroke='%238b5cf6' stroke-width='2'/%3E%3Ctext x='200' y='240' font-family='Arial' font-size='14' fill='%238b5cf6' text-anchor='middle'%3ECone Beam CT%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Cone beam CT 3D reconstruction of full upper dental arch showing bone density and anatomical structures for implant planning',
      annotations: 8,
      notes: 'Implant planning - site #14'
    },
    {
      id: 10,
      type: 'ct',
      category: 'Cone Beam CT',
      toothNumber: '#30',
      date: '2026-01-09',
      time: '02:30 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23ddd6fe'/%3E%3Ccircle cx='200' cy='150' r='80' fill='none' stroke='%238b5cf6' stroke-width='3'/%3E%3Ccircle cx='200' cy='150' r='50' fill='none' stroke='%238b5cf6' stroke-width='2'/%3E%3Ctext x='200' y='240' font-family='Arial' font-size='14' fill='%238b5cf6' text-anchor='middle'%3ECone Beam CT%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Cone beam CT cross-sectional view of mandible showing bone height, width, and proximity to inferior alveolar nerve',
      annotations: 6,
      notes: 'Lower molar implant assessment'
    },
    {
      id: 11,
      type: 'ct',
      category: 'Cone Beam CT',
      toothNumber: 'TMJ',
      date: '2026-01-07',
      time: '09:00 AM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23ddd6fe'/%3E%3Ccircle cx='200' cy='150' r='80' fill='none' stroke='%238b5cf6' stroke-width='3'/%3E%3Ccircle cx='200' cy='150' r='50' fill='none' stroke='%238b5cf6' stroke-width='2'/%3E%3Ctext x='200' y='240' font-family='Arial' font-size='14' fill='%238b5cf6' text-anchor='middle'%3ECone Beam CT%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Cone beam CT of temporomandibular joint showing condyle, articular disc space, and glenoid fossa',
      annotations: 5,
      notes: 'TMJ disorder evaluation'
    },
    {
      id: 12,
      type: 'ct',
      category: 'Cone Beam CT',
      toothNumber: 'Maxilla',
      date: '2025-12-28',
      time: '01:45 PM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23ddd6fe'/%3E%3Ccircle cx='200' cy='150' r='80' fill='none' stroke='%238b5cf6' stroke-width='3'/%3E%3Ccircle cx='200' cy='150' r='50' fill='none' stroke='%238b5cf6' stroke-width='2'/%3E%3Ctext x='200' y='240' font-family='Arial' font-size='14' fill='%238b5cf6' text-anchor='middle'%3ECone Beam CT%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Cone beam CT sagittal view of maxillary sinus showing sinus floor elevation and available bone for implant placement',
      annotations: 7,
      notes: 'Sinus lift evaluation - posterior maxilla'
    },

    // Intraoral Clinical Photos - Anterior Views
    {
      id: 13,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Anterior',
      date: '2026-01-08',
      time: '03:45 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph of anterior teeth showing discoloration on central incisors before whitening treatment',
      annotations: 1,
      notes: 'Before whitening - baseline documentation'
    },
    {
      id: 14,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Anterior',
      date: '2026-01-22',
      time: '04:00 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph of anterior teeth after whitening treatment showing improved brightness and uniform color',
      annotations: 1,
      notes: 'After whitening - 2 weeks post-treatment'
    },
    {
      id: 15,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Anterior',
      date: '2026-01-06',
      time: '11:30 AM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing smile line and gingival contours for cosmetic evaluation',
      annotations: 2,
      notes: 'Cosmetic consultation - smile design'
    },

    // Intraoral Clinical Photos - Occlusal Views
    {
      id: 16,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Upper Arch',
      date: '2026-01-04',
      time: '01:00 PM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Occlusal view of upper dental arch showing tooth arrangement, palatal anatomy, and occlusal surfaces',
      annotations: 3,
      notes: 'Orthodontic assessment - upper arch'
    },
    {
      id: 17,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Lower Arch',
      date: '2026-01-04',
      time: '01:02 PM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Occlusal view of lower dental arch showing tooth positions, tongue space, and occlusal plane',
      annotations: 2,
      notes: 'Orthodontic assessment - lower arch'
    },

    // Intraoral Clinical Photos - Lateral Views
    {
      id: 18,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Right Side',
      date: '2026-01-03',
      time: '10:15 AM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Right lateral intraoral photograph showing buccal surfaces and occlusal relationship of posterior teeth',
      annotations: 1,
      notes: 'Occlusion check - right side'
    },
    {
      id: 19,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Left Side',
      date: '2026-01-03',
      time: '10:17 AM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Left lateral intraoral photograph displaying buccal tooth surfaces and bite alignment',
      annotations: 1,
      notes: 'Occlusion check - left side'
    },

    // Intraoral Clinical Photos - Specific Conditions
    {
      id: 20,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#8',
      date: '2026-01-02',
      time: '02:45 PM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Close-up intraoral photograph of fractured central incisor showing extent of damage to enamel and dentin',
      annotations: 4,
      notes: 'Fractured tooth - emergency visit'
    },
    {
      id: 21,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#14',
      date: '2025-12-31',
      time: '09:30 AM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing dental caries on occlusal surface of upper first molar',
      annotations: 2,
      notes: 'Caries documentation - treatment needed'
    },
    {
      id: 22,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#3',
      date: '2025-12-29',
      time: '04:30 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph of completed crown preparation on upper right first molar with clear margin lines',
      annotations: 3,
      notes: 'Crown prep - ready for impression'
    },
    {
      id: 23,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#3',
      date: '2026-01-11',
      time: '03:00 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing final crown cementation with excellent marginal fit and color match',
      annotations: 2,
      notes: 'Crown cementation - final result'
    },
    {
      id: 24,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#19',
      date: '2025-12-30',
      time: '03:45 PM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing completed composite restoration on lower left first molar with natural tooth color',
      annotations: 1,
      notes: 'Composite restoration - post-operative'
    },

    // Intraoral Clinical Photos - Periodontal
    {
      id: 25,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: 'Lower Anterior',
      date: '2025-12-27',
      time: '10:00 AM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing gingival recession on lower anterior teeth exposing root surfaces',
      annotations: 5,
      notes: 'Gingival recession - grafting evaluation'
    },
    {
      id: 26,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#3-5',
      date: '2025-12-22',
      time: '02:30 PM',
      provider: 'Dr. Chen',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph displaying heavy calculus deposits on lingual surfaces of lower anterior teeth',
      annotations: 2,
      notes: 'Calculus buildup - scaling needed'
    },

    // Intraoral Clinical Photos - Prosthetics
    {
      id: 27,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#13-15',
      date: '2025-12-18',
      time: '11:45 AM',
      provider: 'Dr. Anderson',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph of three-unit fixed bridge replacing missing tooth with natural appearance',
      annotations: 3,
      notes: 'Fixed bridge - final cementation'
    },
    {
      id: 28,
      type: 'intraoral',
      category: 'Clinical Photo',
      toothNumber: '#8',
      date: '2025-12-15',
      time: '04:15 PM',
      provider: 'Dr. Martinez',
      status: 'reviewed',
      thumbnail: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23dcfce7'/%3E%3Ccircle cx='200' cy='150' r='60' fill='%2322c55e' opacity='0.3'/%3E%3Cpath d='M170 140 Q200 120 230 140' stroke='%2322c55e' stroke-width='3' fill='none'/%3E%3Ctext x='200' y='230' font-family='Arial' font-size='14' fill='%2322c55e' text-anchor='middle'%3EClinical Photo%3C/text%3E%3C/svg%3E",
      thumbnailAlt: 'Intraoral photograph showing dental implant crown on central incisor with excellent esthetics',
      annotations: 4,
      notes: 'Implant crown - final restoration'
    }
  ];

  const handleImageSelect = (image) => {
    setSelectedImage(image);
    setActiveView('viewer');
    // Load annotations for selected image
    const imageAnnotations = imagingData?.find(img => img?.id === image?.id)?.annotationsList || [];
    setAnnotations(imageAnnotations);
  };

  const handleImageAcquired = (newImage) => {
    // Handle newly acquired image from X-ray machine
    console.log('Image acquired:', newImage);
    // In production, this would refresh the image library from Supabase
    setShowXrayConnection(false);
    // Show success notification
    alert('Image acquired successfully!');
  };

  const handleAddAnnotation = (annotation) => {
    setAnnotations([...annotations, { ...annotation, id: Date.now() }]);
  };

  const handleDeleteAnnotation = (annotationId) => {
    setAnnotations(annotations?.filter(a => a?.id !== annotationId));
  };

  const handleCompareImages = () => {
    setActiveView('comparison');
  };

  const handleToothClick = (toothNumber) => {
    setSelectedTooth(toothNumber);
    setShowToothModal(true);
  };

  const handleSaveToothNote = (noteData) => {
    setToothNotes(prev => ({
      ...prev,
      [noteData?.toothNumber]: noteData
    }));
  };

  const handleDeleteToothNote = (toothNumber) => {
    setToothNotes(prev => {
      const updated = { ...prev };
      delete updated?.[toothNumber];
      return updated;
    });
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <PatientContextBar 
          patient={currentPatient} 
          alerts={patientAlerts}
          onAlertClick={() => {}}
        />
        <QuickActions />
        <SessionManager 
          onSessionExpired={() => {}}
          onSessionExtended={() => {}}
        />
        
        <main className="flex-1 overflow-y-auto p-2 sm:p-4 lg:p-6">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-1 sm:mb-2">
                  Dental Imaging Management
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  View, annotate, and manage dental images with advanced visualization tools
                </p>
              </div>
              
              {/* Desktop View Controls */}
              <div className="hidden md:flex flex-wrap items-center gap-2">
                <Button
                  variant={activeView === 'viewer' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Eye"
                  onClick={() => setActiveView('viewer')}
                >
                  Viewer
                </Button>
                <Button
                  variant={activeView === 'comparison' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Columns"
                  onClick={() => setActiveView('comparison')}
                >
                  Compare
                </Button>
                <Button
                  variant={activeView === 'library' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Grid"
                  onClick={() => setActiveView('library')}
                >
                  Library
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Upload"
                  onClick={() => setShowXrayConnection(!showXrayConnection)}
                >
                  {showXrayConnection ? 'Close Connection' : 'Connect X-ray Machine'}
                </Button>
              </div>

              {/* Mobile View Controls */}
              <div className="flex md:hidden gap-2 overflow-x-auto pb-2">
                <Button
                  variant={activeView === 'viewer' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Eye"
                  onClick={() => {
                    setActiveView('viewer');
                    setShowMobileLibrary(false);
                    setShowMobileTools(false);
                  }}
                  className="whitespace-nowrap"
                >
                  Viewer
                </Button>
                <Button
                  variant={activeView === 'comparison' ? 'default' : 'outline'}
                  size="sm"
                  iconName="Columns"
                  onClick={() => {
                    setActiveView('comparison');
                    setShowMobileLibrary(false);
                    setShowMobileTools(false);
                  }}
                  className="whitespace-nowrap"
                >
                  Compare
                </Button>
                <Button
                  variant={showMobileLibrary ? 'default' : 'outline'}
                  size="sm"
                  iconName="Grid"
                  onClick={() => {
                    setShowMobileLibrary(!showMobileLibrary);
                    setShowMobileTools(false);
                  }}
                  className="whitespace-nowrap"
                >
                  Library
                </Button>
                <Button
                  variant={showMobileTools ? 'default' : 'outline'}
                  size="sm"
                  iconName="Wrench"
                  onClick={() => {
                    setShowMobileTools(!showMobileTools);
                    setShowMobileLibrary(false);
                  }}
                  className="whitespace-nowrap"
                >
                  Tools
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Upload"
                  onClick={() => setShowXrayConnection(!showXrayConnection)}
                  className="whitespace-nowrap"
                >
                  X-ray
                </Button>
              </div>
            </div>
          </div>

          {/* Tooth Diagram Section - Collapsible on mobile */}
          <div className="mb-4 sm:mb-6">
            <ToothDiagram 
              onToothClick={handleToothClick}
              toothNotes={toothNotes}
            />
          </div>

          {/* Main Content Area - Responsive Layout */}
          <div className="flex flex-col md:flex-row gap-2 sm:gap-4 h-auto md:h-[calc(100vh-28rem)]">
            {/* Left Panel - Image Library (Desktop) or Mobile Drawer */}
            <div className={`${
              showMobileLibrary 
                ? 'fixed inset-0 z-50 bg-background p-4 overflow-y-auto md:relative md:inset-auto md:z-auto md:p-0' :'hidden md:block'
            } md:w-64 lg:w-80 md:flex-shrink-0 md:overflow-y-auto`}>
              {showMobileLibrary && (
                <div className="flex justify-between items-center mb-4 md:hidden">
                  <h2 className="text-lg font-semibold">Image Library</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setShowMobileLibrary(false)}
                  />
                </div>
              )}
              {showXrayConnection ? (
                <XrayMachineConnection
                  onImageAcquired={handleImageAcquired}
                  patientId={currentPatient?.mrn}
                />
              ) : (
                <ImageLibrary
                  images={imagingData}
                  selectedImage={selectedImage}
                  onImageSelect={(image) => {
                    handleImageSelect(image);
                    setShowMobileLibrary(false);
                  }}
                />
              )}
            </div>

            {/* Center Panel - Main Viewer/Comparison */}
            <div className="flex-1 bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden flex flex-col min-h-[400px] md:min-h-0">
              {activeView === 'viewer' && (
                <ImageViewer
                  image={selectedImage || imagingData?.[0]}
                  annotations={annotations}
                  viewerMode={viewerMode}
                  onViewerModeChange={setViewerMode}
                  showAnnotationTools={showAnnotationTools}
                />
              )}
              
              {activeView === 'comparison' && (
                <ImageComparison
                  images={imagingData}
                  selectedImage={selectedImage}
                />
              )}
              
              {activeView === 'library' && (
                <div className="p-3 sm:p-4 lg:p-6 overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
                    {imagingData?.map((image) => (
                      <div
                        key={image?.id}
                        className="cursor-pointer group"
                        onClick={() => handleImageSelect(image)}
                      >
                        <div className="aspect-square bg-muted rounded-lg overflow-hidden border-2 border-transparent group-hover:border-primary transition-all">
                          <AppImage
                            src={image?.thumbnail}
                            alt={image?.thumbnailAlt}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 sm:mt-2 truncate">
                          {image?.category} - {image?.toothNumber}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel - Tools & Documentation (Desktop) or Mobile Drawer */}
            <div className={`${
              showMobileTools 
                ? 'fixed inset-0 z-50 bg-background p-4 overflow-y-auto md:relative md:inset-auto md:z-auto md:p-0' :'hidden md:block'
            } md:w-64 lg:w-80 md:flex-shrink-0 md:space-y-4 md:overflow-y-auto space-y-3 sm:space-y-4`}>
              {showMobileTools && (
                <div className="flex justify-between items-center mb-4 md:hidden">
                  <h2 className="text-lg font-semibold">Tools & Documentation</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setShowMobileTools(false)}
                  />
                </div>
              )}
              
              {/* Annotation Toolkit */}
              {showAnnotationTools && activeView === 'viewer' && (
                <AnnotationToolkit
                  annotations={annotations}
                  onAddAnnotation={handleAddAnnotation}
                  onDeleteAnnotation={handleDeleteAnnotation}
                />
              )}
              
              {/* Case Documentation */}
              {showCasePanel && (
                <CaseDocumentation
                  image={selectedImage || imagingData?.[0]}
                  onCompare={handleCompareImages}
                />
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Tooth Notes Modal */}
      <ToothNotesModal
        isOpen={showToothModal}
        onClose={() => setShowToothModal(false)}
        toothNumber={selectedTooth}
        existingNote={toothNotes?.[selectedTooth]}
        onSave={handleSaveToothNote}
        onDelete={handleDeleteToothNote}
      />
    </div>
  );
};

export default DentalImagingManagement;